// oxlint-disable-next-line no-restricted-imports
import { fromJS, List, Map, Record, Set } from '@gonfalon/immutable-shim/immutableShim';
import type { CreateFunctionInput } from '@gonfalon/legacy-immutable/immutableUtils';

import { allowDecision } from '#utils/access/allowDecision';
import { createAccessDecision } from '#utils/access/records/AccessDecision';
import type { Member } from '#utils/members/records/Member';
import type { ProjectProps } from '#utils/projects/projectTypes';
import { validateProject } from '#utils/projects/validation';

export const defaultProjectProps = {
  _access: undefined as ProjectProps['_access'],
  _links: Map(),
  key: '',
  _id: '',
  name: '',
  tags: Set(),
  defaultClientSideAvailability: Map({ usingMobileKey: false, usingEnvironmentId: false }),
  environments: List(),
  environmentsTotalCount: 0,
  defaultReleasePipelineKey: '',
};

export class Project extends Record<ProjectProps>(defaultProjectProps) {
  checkAccess({ profile }: { profile: Member }) {
    const access = this._access;

    if (profile.isReader()) {
      return () => createAccessDecision({ isAllowed: false, appliedRoleName: 'Reader' });
    }

    if (profile.hasStrictWriterRights() || !access || !access.get('denied')) {
      return allowDecision;
    }

    return (action?: string) => {
      const deniedAction = access.get('denied').find((v) => v.get('action') === action);
      if (deniedAction) {
        const reason = deniedAction.get('reason');
        const roleName = reason && reason.get('role_name');
        return createAccessDecision({
          isAllowed: false,
          appliedRoleName: roleName,
        });
      }
      return createAccessDecision({ isAllowed: true });
    };
  }

  isNew() {
    return this.key === '';
  }

  validate() {
    return validateProject(this);
  }

  selfLink() {
    return this._links.getIn(['self', 'href']);
  }

  environmentsLink() {
    return this._links.getIn(['environments', 'href']);
  }
}

export function createProject(props?: CreateFunctionInput<Project>) {
  if (!props) {
    return new Project();
  }
  const project = props instanceof Project ? props : new Project(fromJS(props));
  return project.update('tags', (tags: Set<string>) => (tags.size ? tags.toSet() : Set<string>()));
}
